import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Ellipsis } from '@/components/ui/ellipsis';
import { Switch } from '@/components/ui/switch';
import { AssigneesBadge } from '@/components/ui/assignees-badge';
import { CountryFilter } from '@/components/ui/country-select';
import { MultiGeographyBadge } from '@/shared/geography/MultiGeographyBadge';
import { compareGeographyCodesForFilter } from '@/shared/geography';
import {
  useListReferenceSubscriptionsApi,
  useUpdateReferenceSubscriptionApi,
} from '@/api/regulatoryWatch';
import type {
  ReferenceSubscriptionItem,
  ReferenceSubscriptionStatus,
} from '@ditto/shared';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Compass,
  Loader2,
  Search,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useMemo, useRef, useState, type FC } from 'react';
import { SourceDescriptionPopover } from './SourceDescriptionPopover';
import { type SourceItem } from './SourceList';
import { sortSources } from './sortSources';

type TypeFilter = 'all' | 'regulation' | 'guidance';

export type ReferenceRecommendation = {
  referenceId: string;
  sourceId: string;
  referenceTitle: string;
  recommended: boolean;
  reasoning: string;
};

export type RegulationsSourceListProps = {
  sources: SourceItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onBulkToggle?: (sourceIds: string[], subscribe: boolean) => void;
  onAssignSource?: (sourceId: string, memberIds: string[]) => void;
  /** When provided (onboarding), use these as initial ref states instead of fetching from API */
  referenceRecommendations?: ReferenceRecommendation[];
  /** Callback when ref subscription state changes (onboarding) */
  onReferenceToggle?: (referenceId: string, subscribed: boolean) => void;
};

const TypeBadge: FC<{ type: string }> = ({ type }) => {
  if (type === 'regulation') {
    return (
      <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 bg-[#fadff7]">
        <Shield className="size-3.5" />
        Regulation
      </span>
    );
  }
  if (type === 'guidance') {
    return (
      <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 bg-[#d8dcff]">
        <Compass className="size-3.5" />
        Guidance
      </span>
    );
  }
  return null;
};

// ─── Reference rows for an expanded source ───────────────────────────────

const REF_PAGE_SIZE = 10;

function getRefChecked(
  ref: ReferenceSubscriptionItem,
  sourceSubscribed: boolean
): boolean {
  if (ref.status === 'subscribed') return true;
  if (ref.status === 'unsubscribed') return false;
  return sourceSubscribed;
}

const SourceReferenceList: FC<{
  sourceId: string;
  sourceSubscribed: boolean;
  /** Onboarding mode: use recommendations instead of fetching from API */
  recommendations?: ReferenceRecommendation[];
  onRecommendationToggle?: (referenceId: string, subscribed: boolean) => void;
}> = ({
  sourceId,
  sourceSubscribed,
  recommendations,
  onRecommendationToggle,
}) => {
  const isOnboarding = !!recommendations;
  const { data: apiRefs, isPending } =
    useListReferenceSubscriptionsApi(sourceId);
  const { mutate: updateRefSub } = useUpdateReferenceSubscriptionApi();

  // In onboarding mode, build refs from recommendations instead of API
  const refs: ReferenceSubscriptionItem[] | undefined = isOnboarding
    ? recommendations
        .filter(r => r.sourceId === sourceId)
        .map(r => ({
          referenceId: r.referenceId,
          referenceTitle: r.referenceTitle,
          referenceExternalId: '',
          referenceDescription: null,
          status: (r.recommended
            ? 'subscribed'
            : 'unsubscribed') as ReferenceSubscriptionStatus,
          assignedMemberIds: [],
          aiJustification: r.reasoning || null,
        }))
    : apiRefs;
  const [page, setPage] = useState(0);
  const [refSearch, setRefSearch] = useState('');
  const [optimisticRefStates, setOptimisticRefStates] = useState<
    Record<string, boolean>
  >({});

  const getRefIsChecked = (ref: ReferenceSubscriptionItem): boolean => {
    if (ref.referenceId in optimisticRefStates) {
      return optimisticRefStates[ref.referenceId]!;
    }
    return getRefChecked(ref, sourceSubscribed);
  };

  // Capture initial sort order once when refs first load — don't re-sort on toggle
  const initialOrderRef = useRef<string[] | null>(null);
  if (refs && refs.length > 0 && !initialOrderRef.current) {
    initialOrderRef.current = [...refs]
      .sort((a, b) => {
        const aChecked = getRefChecked(a, sourceSubscribed) ? 0 : 1;
        const bChecked = getRefChecked(b, sourceSubscribed) ? 0 : 1;
        if (aChecked !== bChecked) return aChecked - bChecked;
        return a.referenceTitle.localeCompare(b.referenceTitle);
      })
      .map(r => r.referenceId);
  }

  const filteredRefs = useMemo(() => {
    if (!refs || refs.length === 0) return [];
    const filtered = refSearch.trim()
      ? refs.filter(r => {
          const q = refSearch.toLowerCase();
          return (
            r.referenceTitle.toLowerCase().includes(q) ||
            r.referenceExternalId.toLowerCase().includes(q) ||
            r.referenceDescription?.toLowerCase().includes(q)
          );
        })
      : refs;

    // Use initial sort order if available, otherwise sort by checked first
    if (initialOrderRef.current) {
      const orderMap = new Map(initialOrderRef.current.map((id, i) => [id, i]));
      return [...filtered].sort(
        (a, b) =>
          (orderMap.get(a.referenceId) ?? 999) -
          (orderMap.get(b.referenceId) ?? 999)
      );
    }

    return [...filtered].sort((a, b) => {
      const aChecked = getRefIsChecked(a) ? 0 : 1;
      const bChecked = getRefIsChecked(b) ? 0 : 1;
      if (aChecked !== bChecked) return aChecked - bChecked;
      return a.referenceTitle.localeCompare(b.referenceTitle);
    });
  }, [refs, refSearch, sourceSubscribed, optimisticRefStates]);

  const handleToggleRef = (ref: ReferenceSubscriptionItem) => {
    const isChecked = getRefIsChecked(ref);
    const newChecked = !isChecked;
    const newStatus: 'subscribed' | 'unsubscribed' = newChecked
      ? 'subscribed'
      : 'unsubscribed';

    setOptimisticRefStates(prev => ({
      ...prev,
      [ref.referenceId]: newChecked,
    }));

    if (isOnboarding && onRecommendationToggle) {
      // In onboarding, just notify parent — no API call
      onRecommendationToggle(ref.referenceId, newChecked);
    } else {
      updateRefSub(
        { referenceId: ref.referenceId, body: { status: newStatus } },
        {
          onSettled: () => {
            setOptimisticRefStates(prev => {
              const next = { ...prev };
              delete next[ref.referenceId];
              return next;
            });
          },
        }
      );
    }
  };

  if (!isOnboarding && isPending) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="size-4 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!refs || refs.length === 0) {
    return (
      <div className="pl-11 pr-4 py-3 text-sm text-neutral-400">
        No references found.
      </div>
    );
  }

  const totalPages = Math.ceil(filteredRefs.length / REF_PAGE_SIZE);
  const safePage = Math.min(page, totalPages - 1);
  const pageRefs = filteredRefs.slice(
    safePage * REF_PAGE_SIZE,
    (safePage + 1) * REF_PAGE_SIZE
  );

  const handleRefSearchChange = (value: string) => {
    setRefSearch(value);
    setPage(0);
  };

  return (
    <>
      {refs.length > REF_PAGE_SIZE && (
        <div className="pl-11 pr-4 py-2 border-b border-neutral-100">
          <div className="relative w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search references..."
              value={refSearch}
              onChange={e => handleRefSearchChange(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-sm border border-neutral-200 rounded-md bg-white placeholder:text-neutral-400 outline-none focus:border-neutral-400"
            />
          </div>
        </div>
      )}
      {filteredRefs.length === 0 && refSearch.trim() && (
        <div className="pl-11 pr-4 py-3 text-sm text-neutral-400">
          No references match "{refSearch}".
        </div>
      )}
      {pageRefs.map(ref => {
        const checked = getRefIsChecked(ref);
        const refRec = recommendations?.find(
          r => r.referenceId === ref.referenceId
        );
        return (
          <div
            key={ref.referenceId}
            className="flex items-center gap-3 pl-11 pr-4 py-2.5 border-b border-neutral-100 last:border-b-0"
          >
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <Ellipsis
                  hasTooltip
                  className="min-w-0 text-sm text-neutral-700"
                >
                  {ref.referenceTitle}
                </Ellipsis>
                {ref.referenceDescription && (
                  <SourceDescriptionPopover
                    title={ref.referenceTitle}
                    description={ref.referenceDescription}
                  />
                )}
              </div>
              {refRec?.reasoning && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <Sparkles className="size-3 text-magenta-500 shrink-0" />
                  <Ellipsis hasTooltip className="text-xs text-neutral-500">
                    {refRec.reasoning}
                  </Ellipsis>
                </div>
              )}
            </div>
            <Checkbox
              checked={checked}
              onCheckedChange={() => handleToggleRef(ref)}
              className="shrink-0"
            />
          </div>
        );
      })}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pl-11 pr-4 py-2 border-t border-neutral-100">
          <span className="text-xs text-neutral-500">
            {safePage * REF_PAGE_SIZE + 1}-
            {Math.min((safePage + 1) * REF_PAGE_SIZE, filteredRefs.length)} of{' '}
            {filteredRefs.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(safePage - 1)}
              disabled={safePage <= 0}
              className="flex items-center justify-center size-6 rounded border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="size-3.5" />
            </button>
            <span className="text-xs text-neutral-500 px-1">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage(safePage + 1)}
              disabled={safePage >= totalPages - 1}
              className="flex items-center justify-center size-6 rounded border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="size-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Main component ──────────────────────────────────────────────────────

export const RegulationsSourceList: FC<RegulationsSourceListProps> = ({
  sources,
  selectedIds,
  onToggle,
  onBulkToggle,
  onAssignSource,
  referenceRecommendations,
  onReferenceToggle,
}) => {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('regulation');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(
    new Set()
  );

  const availableRegions = useMemo(() => {
    const geos = new Set<string>();
    for (const s of sources) {
      if (s.geography && s.geography !== 'unknown') {
        geos.add(s.geography);
      }
      if (s.institutionGeographies) {
        for (const g of s.institutionGeographies) {
          if (g !== 'unknown') geos.add(g);
        }
      }
    }
    return [...geos].sort(compareGeographyCodesForFilter);
  }, [sources]);

  const filteredSources = useMemo(() => {
    return sources.filter(source => {
      if (typeFilter !== 'all' && !source.referenceTypes.includes(typeFilter))
        return false;
      if (
        selectedRegions.length > 0 &&
        !selectedRegions.includes(source.geography) &&
        !source.institutionGeographies?.some(g => selectedRegions.includes(g))
      )
        return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (
          !source.title.toLowerCase().includes(q) &&
          !source.description?.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [sources, typeFilter, selectedRegions, searchQuery]);

  // Capture initial sort order once — don't re-sort when user toggles
  const initialSourceOrderRef = useRef<string[] | null>(null);
  if (filteredSources.length > 0 && !initialSourceOrderRef.current) {
    initialSourceOrderRef.current = sortSources(
      filteredSources,
      selectedIds
    ).map(s => s.id);
  }

  const sortedSources = useMemo(() => {
    if (initialSourceOrderRef.current) {
      const orderMap = new Map(
        initialSourceOrderRef.current.map((id, i) => [id, i])
      );
      return [...filteredSources].sort(
        (a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999)
      );
    }
    return sortSources(filteredSources, selectedIds);
  }, [filteredSources, selectedIds]);

  const totalSelectedCount = filteredSources.filter(s =>
    selectedIds.has(s.id)
  ).length;
  const allSelected =
    filteredSources.length > 0 && totalSelectedCount === filteredSources.length;

  const toggleSelectAll = () => {
    const subscribe = !allSelected;
    const idsToToggle = filteredSources
      .filter(s => (subscribe ? !selectedIds.has(s.id) : selectedIds.has(s.id)))
      .map(s => s.id);
    if (idsToToggle.length === 0) return;
    if (onBulkToggle) {
      onBulkToggle(idsToToggle, subscribe);
    } else {
      for (const id of idsToToggle) onToggle(id);
    }
  };

  const toggleExpand = (sourceId: string) => {
    setExpandedSources(prev => {
      const next = new Set(prev);
      if (next.has(sourceId)) next.delete(sourceId);
      else next.add(sourceId);
      return next;
    });
  };

  const searchPlaceholder = useMemo(() => {
    if (typeFilter === 'regulation') return 'Search regulations';
    if (typeFilter === 'guidance') return 'Search guidances';
    return 'Search regulations & guidances';
  }, [typeFilter]);

  return (
    <>
      {/* Filters */}
      <div className="px-6 pt-4 pb-3 shrink-0 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center shadow-sm">
            <button
              type="button"
              onClick={() => setTypeFilter('regulation')}
              className={`flex items-center gap-2 px-3 py-[9.5px] min-h-[40px] border border-neutral-200 rounded-l-lg text-sm font-medium -mr-px ${
                typeFilter === 'regulation'
                  ? 'bg-white text-neutral-900'
                  : 'bg-neutral-100 text-neutral-900'
              }`}
            >
              <Shield className="size-4" />
              Regulations
            </button>
            <button
              type="button"
              onClick={() => setTypeFilter('guidance')}
              className={`flex items-center gap-2 px-3 py-[9.5px] min-h-[40px] border border-neutral-200 text-sm font-medium -mr-px ${
                typeFilter === 'guidance'
                  ? 'bg-white text-neutral-900'
                  : 'bg-neutral-100 text-neutral-900'
              }`}
            >
              <Compass className="size-4" />
              Guidances
            </button>
            <button
              type="button"
              onClick={() => setTypeFilter('all')}
              className={`flex items-center gap-2 px-3 py-[9.5px] min-h-[40px] border border-neutral-200 rounded-r-lg text-sm font-medium ${
                typeFilter === 'all'
                  ? 'bg-white text-neutral-900'
                  : 'bg-neutral-100 text-neutral-900'
              }`}
            >
              All
            </button>
          </div>

          <CountryFilter
            label="Regulatory region"
            countries={availableRegions}
            selected={selectedRegions}
            onChange={setSelectedRegions}
          />
        </div>

        <div className="flex items-center gap-2.5">
          <div className="relative w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500" />
            <Input
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex-1 flex items-center justify-end gap-2">
            <span className="text-xs font-medium text-neutral-900">
              Select all
            </span>
            <Switch checked={allSelected} onCheckedChange={toggleSelectAll} />
          </div>
        </div>
      </div>

      {/* Source list */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0 bg-neutral-50">
        <div className="flex flex-col gap-3 pt-4">
          {sortedSources.map(source => {
            const isSubscribed = selectedIds.has(source.id);
            const isExpanded = expandedSources.has(source.id);

            return (
              <div
                key={source.id}
                className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
              >
                {/* Source header */}
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleExpand(source.id)}
                      className="shrink-0"
                    >
                      {isExpanded ? (
                        <ChevronUp className="size-4 text-neutral-500" />
                      ) : (
                        <ChevronDown className="size-4 text-neutral-500" />
                      )}
                    </button>

                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => toggleExpand(source.id)}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-black">
                          {source.title}
                        </span>
                        {source.description && (
                          <SourceDescriptionPopover
                            title={source.title}
                            description={source.description}
                          />
                        )}
                        {source.referenceTypes.map(t => (
                          <TypeBadge key={t} type={t} />
                        ))}
                        <MultiGeographyBadge
                          geography={source.geography}
                          institutionGeographies={source.institutionGeographies}
                          institutionName={source.institutionName}
                          className="h-6"
                        />
                      </div>
                      {source.reasoning && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <Sparkles className="size-3 text-magenta-500 shrink-0" />
                          <Ellipsis
                            hasTooltip
                            className="text-xs text-neutral-500"
                          >
                            {source.reasoning}
                          </Ellipsis>
                        </div>
                      )}
                    </div>

                    {isSubscribed && onAssignSource && (
                      <div onClick={e => e.stopPropagation()}>
                        <AssigneesBadge
                          assignedMemberIds={source.assignedMemberIds ?? []}
                          onAssign={memberIds =>
                            onAssignSource(source.id, memberIds)
                          }
                        />
                      </div>
                    )}

                    <Checkbox
                      checked={isSubscribed}
                      onCheckedChange={() => onToggle(source.id)}
                      className="shrink-0"
                    />
                  </div>
                </div>

                {/* Expanded references */}
                {isExpanded && (
                  <SourceReferenceList
                    sourceId={source.id}
                    sourceSubscribed={isSubscribed}
                    recommendations={referenceRecommendations}
                    onRecommendationToggle={onReferenceToggle}
                  />
                )}
              </div>
            );
          })}

          {sortedSources.length === 0 && (
            <div className="border rounded-lg p-6 text-center text-sm text-muted-foreground">
              No sources match the selected filters.
            </div>
          )}
        </div>
      </div>
    </>
  );
};
